/**
 * Persists Google Ads click IDs and marketing UTMs in sessionStorage so they survive
 * SPA navigation and OAuth redirects on app.trulyinbox.com, then pushes signup_complete
 * for GTM / Google Ads conversion tagging.
 *
 * (Marketing site localStorage does not share across origins with the app.)
 */

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

const STORAGE_KEY = 'ti_app_marketing_attribution';

/** Params forwarded from ads landing URLs and trulyinbox.com signup links. */
export const MARKETING_ATTRIBUTION_PARAM_KEYS = [
  'gclid',
  'gbraid',
  'wbraid',
  'gad_source',
  'gad_campaignid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'ref',
] as const;

export type MarketingAttributionRecord = Partial<
  Record<typeof MARKETING_ATTRIBUTION_PARAM_KEYS[number], string>
>;

/** Event name for GTM Custom Event trigger — coordinate with performance marketing. */
export const GTM_SIGNUP_COMPLETE_EVENT = 'signup_complete';

export type GtmSignupMethod = 'email' | 'oauth_google' | 'oauth_microsoft';

function getDataLayer(): Record<string, unknown>[] {
  if (typeof window === 'undefined') {
    return [];
  }
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  return window.dataLayer;
}

/** First-touch merge: keep existing values; only add keys not yet stored. */
export function mergeMarketingAttributionFromSearch(search: string): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }

  const trimmed = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(trimmed);

  let merged: MarketingAttributionRecord = {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      merged = JSON.parse(raw) as MarketingAttributionRecord;
    }
  } catch {
    merged = {};
  }

  let changed = false;
  MARKETING_ATTRIBUTION_PARAM_KEYS.forEach((key) => {
    const val = params.get(key);
    if (val && merged[key] === undefined) {
      merged[key] = val;
      changed = true;
    }
  });

  if (changed) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  }
}

export function mergeMarketingAttributionFromLocation(): void {
  if (typeof window === 'undefined') {
    return;
  }
  mergeMarketingAttributionFromSearch(window.location.search || '');
}

export function getPersistedMarketingAttribution(): MarketingAttributionRecord {
  if (typeof sessionStorage === 'undefined') {
    return {};
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    return JSON.parse(raw) as MarketingAttributionRecord;
  } catch {
    return {};
  }
}

export function clearPersistedMarketingAttribution(): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  sessionStorage.removeItem(STORAGE_KEY);
}

export interface PushGtmSignupCompleteOptions {
  signupMethod: GtmSignupMethod;
}

export function pushGtmSignupComplete(
  options: PushGtmSignupCompleteOptions,
): void {
  const dataLayer = getDataLayer();
  const attribution = getPersistedMarketingAttribution();

  dataLayer.push({
    event: GTM_SIGNUP_COMPLETE_EVENT,
    signup_method: options.signupMethod,
    ...attribution,
  });

  clearPersistedMarketingAttribution();
}

export function oauthMethodToGtmSignupMethod(
  method: string,
): GtmSignupMethod | null {
  if (method === 'google') {
    return 'oauth_google';
  }
  if (method === 'microsoft') {
    return 'oauth_microsoft';
  }
  return null;
}
